import { type ComponentType, lazy } from 'react';

import { isDynamicImportError, logError, reloadOnce } from './log';

// A valid lazy module's `default` must be a renderable component — a function
// (function/class components) or a non-null object (React.memo/forwardRef/lazy
// wrappers carry a `$$typeof` object). A `null`/string/number/boolean default (e.g.
// a stale chunk that resolved to something unusable) would otherwise crash React with
// a generic "element type is invalid" error, bypassing the reload recovery below.
function isRenderableDefault(value: unknown): boolean {
  return typeof value === 'function' || (typeof value === 'object' && value !== null);
}

// Exported for unit testing.
export async function loadWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): Promise<{ default: T }> {
  try {
    const module = await factory();
    if (module && isRenderableDefault(module.default)) {
      return module;
    }
    // Resolved but empty/malformed module → fall through to the stale-chunk recovery.
  } catch (err) {
    const text = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    if (!isDynamicImportError(text)) {
      throw err; // genuine module-eval error, not a chunk-load failure
    }
    // Dynamic-import rejection → reload once to pull the current bundle.
    if (reloadOnce()) {
      // Navigating away — suspend forever so React never renders a broken module.
      return new Promise<{ default: T }>(() => {});
    }
    // Reload guard armed (persistent failure). ErrorBoundary reloads-and-returns for a
    // dynamic-import error WITHOUT logging, so record telemetry HERE (with the failed
    // asset URL) before rethrowing the original — otherwise a persistently-failing chunk
    // (the open nginx stale-asset root cause) would be silently dropped.
    logError(`lazyWithReload: dynamic import failed after reload guard exhausted: ${text}`);
    throw err;
  }
  // Resolved but empty/malformed module (no exception thrown) → same reload recovery.
  if (reloadOnce()) {
    return new Promise<{ default: T }>(() => {});
  }
  logError('lazyWithReload: module resolved without a renderable default after reload guard exhausted');
  // Surface a recognized dynamic-import error so ErrorBoundary shows the fallback.
  throw new Error('Importing a module script failed');
}

// Drop-in replacement for React.lazy for first-party route chunks.
export function lazyWithReload<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(() => loadWithReload(factory));
}
